import {
  getDeleteApiInstitutionIdMutationOptions,
  type InstitutionViewDTO,
} from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Avatar, Badge, Icon, openModal } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Institutions.module.css';

import { DEVICES_INSTITUTION_FIELD, devicesFilteredBy } from '@/ui/pages/Devices/devicesModel';
import { laboratoriesFilteredByInstitution } from '@/ui/pages/Laboratories/laboratoriesModel';
import { usersFilteredByInstitution } from '@/ui/pages/Users/usersModel';
import { MobileCardActions, type RowActionEntry } from '@/ui/shared/RowActions';
import { apiAssetUrl } from '@/utils/apiAssetUrl';

function MobileCard({ row }: { row: InstitutionViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card reads the DTO the list query returns, so there is no page-level row mapper.
  const id = row.id ?? '';
  // HS-847: delete via server-ops (invalidates the institution list query + toast).
  const del = useEntityDelete({
    entity: 'institution',
    mutationOptions: getDeleteApiInstitutionIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('institutes.deleteTitle', { name: row.name }),
      description: t('institutes.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id });
  }, [del, id, row.name, t]);

  // HS-1396: the keys mirror `./RowActions.tsx` entry for entry. The two menus are built in
  // separate files, so a gate added to one silently misses the other — which is how Edit and
  // Delete stayed reachable on a phone for every session. The parity test in
  // `../rowActionParity.test.ts` is what stops them drifting again.
  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      icon: 'edit-01',
      permission: 'institution.edit',
      onClick: () => {
        void navigate({ to: '/institutions/$id/edit', params: { id } });
      },
    },
    null,
    {
      label: t('institutes.actions.viewLaboratories'),
      icon: 'lab',
      onClick: () => {
        void navigate({ to: '/laboratories', search: laboratoriesFilteredByInstitution(id) });
      },
    },
    {
      label: t('institutes.actions.viewDevices'),
      icon: 'tv-01',
      onClick: () => {
        void navigate({
          to: '/devices',
          search: devicesFilteredBy(DEVICES_INSTITUTION_FIELD, id),
        });
      },
    },
    {
      label: t('institutes.actions.viewUsers'),
      icon: 'users-02',
      onClick: () => {
        void navigate({ to: '/users', search: usersFilteredByInstitution(id) });
      },
    },
    null,
    {
      label: t('common.delete'),
      icon: 'trash-01',
      permission: 'institution.delete',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <div className={styles['m-card']}>
      <Link to="/institutions/$id" params={{ id }} className={styles['m-card-title']}>
        {/* HS-1243: the same logo the desktop row shows, so the mobile branch does not disagree
            with it about whether this institution has one. */}
        <Avatar src={apiAssetUrl(row.logoIconUrl)} name={row.name ?? ''} size="sm" />
        {row.name}
      </Link>
      <div className={styles['m-card-badges']}>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="hash-02" />}>
          {row.accountNumber}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="marker-pin-02" />}>
          {row.city}
        </Badge>
        <Badge variant="modern" size="sm">
          <span className={styles['m-badge-avatar']}>
            <Avatar name={row.contact ?? ''} size="xs" />
            {row.contact}
          </span>
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="phone" />}>
          {row.phoneNumber}
        </Badge>
        <Badge variant="modern" size="sm" iconLeading={<Icon name="mail-01" />}>
          {row.email}
        </Badge>
      </div>
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('institutes.columns.city')}</span>
        <span className={styles['m-card-meta-value']}>{row.city}</span>
      </div>
      <MobileCardActions items={items} />
    </div>
  );
}

export default MobileCard;
